import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Shield, ShieldOff, ShieldCheck, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SettingsLayout } from './SettingsLayout';
import { get2FAStatus, setup2FA, verify2FA, disable2FA } from '@/api/settings';

export function TwoFactorPage() {
  const queryClient = useQueryClient();
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: get2FAStatus,
  });

  const setupMutation = useMutation({
    mutationFn: setup2FA,
    onSuccess: (result) => {
      if (result.success && result.qr_code && result.secret_key) {
        setQrCode(result.qr_code);
        setSecretKey(result.secret_key);
        setError(null);
      } else {
        setError(result.error || 'Failed to setup 2FA');
      }
    },
    onError: () => {
      setError('Failed to setup 2FA');
    },
  });

  const verifyMutation = useMutation({
    mutationFn: verify2FA,
    onSuccess: (result) => {
      if (result.success) {
        setSuccess('Two-factor authentication has been enabled!');
        setQrCode(null);
        setSecretKey(null);
        setVerificationCode('');
        queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      } else {
        setError(result.error || 'Invalid verification code');
      }
    },
    onError: () => {
      setError('Failed to verify code');
    },
  });

  const disableMutation = useMutation({
    mutationFn: disable2FA,
    onSuccess: (result) => {
      if (result.success) {
        setSuccess('Two-factor authentication has been disabled.');
        setDisableCode('');
        queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      } else {
        setError(result.error || 'Invalid verification code');
      }
    },
    onError: () => {
      setError('Failed to disable 2FA');
    },
  });

  const handleSetup = () => {
    setError(null);
    setSuccess(null);
    setupMutation.mutate();
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    verifyMutation.mutate(verificationCode);
  };

  const handleDisable = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    disableMutation.mutate(disableCode);
  };

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  const is2FAEnabled = data?.enabled;

  return (
    <SettingsLayout>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {success && (
            <Alert className="bg-green-50 border-green-200">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 ml-2">
                {success}
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {is2FAEnabled ? (
            /* 2FA is enabled - show disable option */
            <>
              <Alert className="bg-green-50 border-green-200">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 ml-2">
                  Two-factor authentication is enabled for your account.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                Two-factor authentication adds an extra layer of security to your account. 
                In addition to your password, you'll need to enter a code from your authenticator app when logging in.
              </p>
              
              <form onSubmit={handleDisable} className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="disableCode">Verification Code</Label>
                  <Input
                    id="disableCode"
                    type="text"
                    placeholder="Enter the 6-digit code from your authenticator app"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your current verification code to confirm disabling 2FA.
                  </p>
                </div>
                <Button 
                  type="submit" 
                  variant="destructive"
                  disabled={disableMutation.isPending}
                >
                  {disableMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Disabling...
                    </>
                  ) : (
                    <>
                      <ShieldOff className="h-4 w-4 mr-2" />
                      Disable Two-Factor Authentication
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : qrCode && secretKey ? (
            /* QR code setup phase */
            <>
              <p className="text-sm text-muted-foreground">
                Two-factor authentication adds an extra layer of security to your account. 
                In addition to your password, you'll need to enter a code from your authenticator app when logging in.
              </p>
              
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="ml-2">
                  Scan the QR code below with your authenticator app (like Google Authenticator, Authy, or Microsoft Authenticator).
                </AlertDescription>
              </Alert>
              
              <div className="flex justify-center py-4">
                <img src={qrCode} alt="QR Code" className="max-w-[250px]" />
              </div>
              
              <Alert className="border-yellow-300 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800 ml-2">
                  If you can't scan the QR code, you can manually enter this secret key: <strong className="font-mono">{secretKey}</strong>
                </AlertDescription>
              </Alert>
              
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verificationCode">Verification Code</Label>
                  <Input
                    id="verificationCode"
                    type="text"
                    placeholder="Enter the 6-digit code from your authenticator app"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    maxLength={6}
                    required
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={verifyMutation.isPending}
                >
                  {verifyMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Verify and Enable
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* Initial state - show setup button */
            <>
              <p className="text-sm text-muted-foreground">
                Two-factor authentication adds an extra layer of security to your account. 
                In addition to your password, you'll need to enter a code from your authenticator app when logging in.
              </p>
              <Button 
                onClick={handleSetup}
                disabled={setupMutation.isPending}
              >
                {setupMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Set Up Two-Factor Authentication
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
